import React, { useContext, useEffect, useState } from 'react'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import API from '../../../services/API'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import { Typography } from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'
import DetailsBox from '../../../components/Cards/DetailsBox'
import moment from 'moment'
import { TenantSettingsContext } from '../../../App'
import { vatMultiplierForItem } from '../../../helpers/region'

const lineItemsTotal = (items, region) =>
  (items || []).reduce(
    (total, item) =>
      total +
      (Number(item.quantity) || 0) *
        (Number(item.unitPrice) || 0) *
        vatMultiplierForItem(item.vat, region),
    0,
  )

// VAT-inclusive gross, net of credit notes and the flat discount - matches
// the server's netAmount maths
const getInvoiceTotal = (invoice, region) => {
  const credited = (invoice?.creditNotes || []).reduce(
    (total, note) => total + lineItemsTotal(note.items, region),
    0,
  )
  return (
    lineItemsTotal(invoice?.items, region) -
    credited -
    Number(invoice?.discount || 0)
  )
}

const getOutstanding = (invoice, region) =>
  Math.max(0, getInvoiceTotal(invoice, region) - Number(invoice?.totalPaid || 0))

const Invoices = ({ clientID, orgType }) => {
  const { orgConfig, tenantInfo } = useContext(TenantSettingsContext)
  const currency = orgConfig?.orgSettings?.currency ?? '£'
  const region = tenantInfo?.region
  const navigate = useNavigate()
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)

  useEffect(() => {
    API.get(`/invoices/perclient?clientID=${clientID}`)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setInvoices(res.data)
        } else {
          setFetchError(res?.data?.error || true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  return (
    <DetailsBox title="Invoices">
      {fetchError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {typeof fetchError === 'string'
            ? fetchError
            : 'Unable to load invoices right now. Please try again later.'}
        </Alert>
      )}
      <CustomDataGrid
        rows={invoices}
        loading={loading}
        maxHeight="300px"
        getRowId={(params) => params._id}
        onRowClick={({ row }) => navigate(`/invoices/${row._id}`)}
        sx={{
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
        }}
        slots={{
          footer: () => (
            <Typography
              padding={1}
              textAlign="right"
              variant="caption"
              color="primary.main"
            >
              You can add a new Invoice from the{' '}
              {orgType === 'PTS' ? 'PTS Booking' : 'Event Page'}
            </Typography>
          ),
        }}
        localeText={{ noRowsLabel: 'No Invoices' }}
        disableColumnResize
        columns={[
          {
            field: 'reference',
            headerName: 'Reference',
            minWidth: 150,
          },
          {
            field: 'invoiceDate',
            headerName: 'Invoice Date',
            renderCell: ({ value }) =>
              value ? moment(value).format('DD/MM/YYYY') : '',
            minWidth: 130,
          },
          {
            field: 'dueDate',
            headerName: 'Due Date',
            renderCell: ({ value }) =>
              value ? moment(value).format('DD/MM/YYYY') : '',
            minWidth: 130,
          },
          {
            field: 'description',
            headerName: 'Description',
            valueGetter: (value, row) =>
              row.event?.title || row.ptsBooking?.bookingRef || '',
            renderCell: ({ row }) =>
              row.event ? (
                <Link
                  to={`/events/${row.event._id}`}
                  target="_blank"
                  style={{ color: '#181818' }}
                  onClick={(event) => event.stopPropagation()}
                >
                  {row.event.title}
                </Link>
              ) : row.ptsBooking ? (
                <Link
                  to={`/pts/bookings/${row.ptsBooking._id}`}
                  target="_blank"
                  style={{ color: '#181818' }}
                  onClick={(event) => event.stopPropagation()}
                >
                  {row.ptsBooking.bookingRef}
                </Link>
              ) : (
                ''
              ),
            minWidth: 200,
            flex: 1,
          },
          {
            field: 'status',
            headerName: 'Status',
            minWidth: 130,
            renderCell: ({ value }) => (
              <Chip
                size="small"
                label={value || 'Draft'}
                color={
                  value === 'Paid'
                    ? 'success'
                    : value === 'Overdue'
                      ? 'error'
                      : value === 'Draft'
                        ? 'default'
                        : 'primary'
                }
              />
            ),
          },
          {
            field: 'total',
            headerName: 'Total',
            renderCell: ({ row }) =>
              `${currency}${getInvoiceTotal(row, region).toFixed(2)}`,
            minWidth: 110,
          },
          {
            field: 'outstanding',
            headerName: 'Outstanding',
            renderCell: ({ row }) =>
              `${currency}${getOutstanding(row, region).toFixed(2)}`,
            minWidth: 110,
          },
        ]}
      />
    </DetailsBox>
  )
}

export default Invoices
